/**
 * @fileoverview AR-805/AR-670 — Google Workspace connection card (PRD G1/G7).
 * The connection is PER USER and belongs to the TEACHER profile: each tutor
 * connects their own Google account (a school holds one connection per
 * teacher). The manager profile supervises — it sees the linked-courses table
 * with the Google account behind each link, but gets no connect/disconnect UI
 * (a manager who also teaches switches to the Teacher profile to connect).
 * Layout copied from the prototype
 * (components/Settings/Integrations/GoogleClassroomIntegration.tsx), adapted
 * to Mantine v6 and to the real backend contract (PR #1384): connect is a
 * 2-step OAuth redirect and the callback lands back on this page with
 * `?code=`.
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Badge, Button, Group, Paper, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconAlertTriangle, IconCheck, IconPlugConnected, IconRefresh, IconX } from '@tabler/icons-react'

import { notifications } from 'context'
import { useCurrentUser } from 'context/useCurrentUser'
import { GoogleClassroomLogo } from 'components/GoogleClassroomLogo'
import { getClassroomCapabilities, useClassroomConnection } from 'integrations/classroom'
import { CourseLinker } from './CourseLinker'
import { DisconnectConfirmModal } from './DisconnectConfirmModal'
import { ConnectionBodySkeleton } from './skeletons'
import { useOauthCallback } from './useOauthCallback'

function formatConnectedSince(iso: string, locale: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })
}

export function GoogleClassroomIntegration() {
  const { t, i18n } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { status, info, connecting, completing, disconnecting, connect, completeConnect, disconnect } =
    useClassroomConnection()
  const [disconnectOpened, { open: openDisconnect, close: closeDisconnect }] = useDisclosure(false)
  const locale = i18n.language || 'pt-BR'
  const tr = (key: string) => t(`classroom.${key}`)
  // Managers supervise (table only); the connection is a Teacher-profile
  // action. `undefined` = profile still loading — render neither variant, so
  // a manager never sees the connect card flash.
  const profileKnown = currentUser !== undefined
  const isManager = currentUser?.profile_used === 'manager'

  const notify = (ok: boolean, successKey: string, failureKey: string) =>
    notifications.show({
      message: tr(ok ? successKey : failureKey),
      color: ok ? 'green' : 'red',
      icon: ok ? <IconCheck size={18} /> : <IconX size={18} />,
    })

  // Deps intentionally empty: the callbacks only fire on the OAuth return.
  useOauthCallback({
    completeConnect,
    onSuccess: useCallback(
      () => notify(true, 'gcwConnectedNotification', 'gcwConnectionFailed'),
      [],
    ),
    onError: useCallback(
      () => notify(false, 'gcwConnectedNotification', 'gcwConnectionFailed'),
      [],
    ),
    onCancelled: useCallback(
      () => notifications.show({ message: tr('gcwConnectCancelled') }),
      [],
    ),
  })

  // Connect and Reauthorize are the same OAuth flow (backend contract).
  const handleConnect = async () => {
    const ok = await connect()
    if (!ok) notify(false, 'gcwConnectedNotification', 'gcwConnectionFailed')
  }
  // Disconnecting stops the sync of every link backed by THIS user's
  // connection (colleagues are untouched) — still the most destructive action
  // here, so it asks first (unlinking a single project already does).
  const handleDisconnect = async () => {
    closeDisconnect()
    notify(await disconnect(), 'gcwDisconnectedNotification', 'gcwDisconnectFailed')
  }

  const loading = status === 'loading' || completing

  // Backend answers 401 for profiles that cannot manage the integration —
  // the contract says to hide the card instead of offering a dead Connect.
  if (status === 'unauthorized') return null

  if (!profileKnown) {
    return (
      <Paper withBorder p='lg' radius='md'>
        <ConnectionBodySkeleton />
      </Paper>
    )
  }

  // Manager profile: supervision only — the table (with the Google account
  // behind each link), no connect/disconnect UI.
  if (isManager) {
    return (
      <Stack spacing='lg'>
        {getClassroomCapabilities().managerCourseLinks && <CourseLinker connected />}
      </Stack>
    )
  }

  return (
    <Stack spacing='lg'>
      <Paper withBorder p='lg' radius='md'>
        <Stack spacing='md'>
          <Group position='apart' align='center'>
            <Group spacing='xs'>
              <GoogleClassroomLogo size={20} />
              <Text weight={600}>{tr('gcwWorkspaceSectionTitle')}</Text>
            </Group>
            {!loading && status === 'connected' && (
              <Badge color='green' variant='light' leftSection={<IconCheck size={14} />}>
                {tr('gcwConnected')}
              </Badge>
            )}
            {!loading && status === 'needs_reconnection' && (
              <Badge color='orange' variant='light' leftSection={<IconAlertTriangle size={14} />}>
                {tr('gcwNeedsReauthTitle')}
              </Badge>
            )}
          </Group>

          {loading && <ConnectionBodySkeleton />}

          {!loading && status === 'disconnected' && (
            <>
              <Text size='sm' color='dimmed'>
                {tr('gcwWorkspaceNotConnectedDescription')}
              </Text>
              <Group>
                <Button
                  leftIcon={<IconPlugConnected size={16} />}
                  loading={connecting}
                  onClick={handleConnect}
                >
                  {tr('gcwConnectWorkspace')}
                </Button>
              </Group>
            </>
          )}

          {!loading && status === 'connected' && (
            <Group position='apart' align='flex-end'>
              <Stack spacing={2}>
                {/* "Connected as <email>" — teachers juggle personal and
                    institutional Google accounts; the email says WHICH one
                    backs this connection. */}
                {info?.googleEmail && (
                  <Text size='sm'>
                    <Text span color='dimmed'>
                      {tr('gcwConnectedAs')}:{' '}
                    </Text>
                    <Text span weight={500}>
                      {info.googleEmail}
                    </Text>
                  </Text>
                )}
                <Text size='sm'>
                  <Text span color='dimmed'>
                    {tr('gcwWorkspaceDomain')}:{' '}
                  </Text>
                  <Text span weight={500}>
                    {info?.workspaceDomain || '—'}
                  </Text>
                </Text>
                {info?.connectedAt && (
                  <Text size='sm'>
                    <Text span color='dimmed'>
                      {tr('gcwConnectedSince')}:{' '}
                    </Text>
                    <Text span weight={500}>
                      {formatConnectedSince(info.connectedAt, locale)}
                    </Text>
                  </Text>
                )}
              </Stack>
              <Button variant='default' color='red' loading={disconnecting} onClick={openDisconnect}>
                {tr('gcwDisconnect')}
              </Button>
            </Group>
          )}

          {!loading && status === 'needs_reconnection' && (
            <Alert
              color='orange'
              variant='light'
              icon={<IconAlertTriangle size={16} />}
              title={tr('gcwNeedsReauthTitle')}
            >
              <Stack spacing='xs'>
                <Text size='sm'>{tr('gcwNeedsReauthDescription')}</Text>
                <Group>
                  <Button
                    size='xs'
                    leftIcon={<IconRefresh size={14} />}
                    loading={connecting}
                    onClick={handleConnect}
                  >
                    {tr('gcwReauthorize')}
                  </Button>
                  <Button
                    size='xs'
                    variant='default'
                    loading={disconnecting}
                    disabled={connecting}
                    onClick={openDisconnect}
                  >
                    {tr('gcwDisconnect')}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}
        </Stack>
      </Paper>

      {getClassroomCapabilities().managerCourseLinks && (
        <CourseLinker connected={status === 'connected' || status === 'needs_reconnection'} />
      )}

      <DisconnectConfirmModal
        opened={disconnectOpened}
        loading={disconnecting}
        onCancel={closeDisconnect}
        onConfirm={handleDisconnect}
      />
    </Stack>
  )
}
